import { Alert, Button, Drawer, Form, Input, Space, Typography } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { DEFAULT_BULK_EMAIL_BODY, DEFAULT_BULK_EMAIL_SUBJECT } from '../constants/bulkEmailDefaults'
import { applyEmailTemplate } from '../hooks/bulkEmailTemplate'
import { useSendBulkNotification } from '../hooks/useSendBulkNotification'

const { Text, Paragraph } = Typography

export const BulkEmailDrawer = ({ visible, recipients, onClose }) => {
  const [form] = Form.useForm()
  const [previewBody, setPreviewBody] = useState('')
  const { sendBulkEmails, loading } = useSendBulkNotification()
  const previewRecipient = recipients.find(recipient => recipient.email) || recipients[0]
  const recipientCount = recipients.length
  const recipientsWithEmail = recipients.filter(recipient => recipient.email).length

  const updatePreview = useCallback((body) => {
    if (!previewRecipient || !body) {
      setPreviewBody('')
      return
    }

    const previewName = [previewRecipient.firstName, previewRecipient.lastName].filter(Boolean).join(' ').trim() || 'Colleague'
    setPreviewBody(applyEmailTemplate(body, {
      name: previewName,
      firstName: previewRecipient.firstName || '',
      lastName: previewRecipient.lastName || ''
    }))
  }, [previewRecipient])

  useEffect(() => {
    if (!visible) return
    form.setFieldsValue({
      subject: DEFAULT_BULK_EMAIL_SUBJECT,
      body: DEFAULT_BULK_EMAIL_BODY
    })
    updatePreview(DEFAULT_BULK_EMAIL_BODY)
  }, [visible, updatePreview, form, previewRecipient])

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  const handleSubmit = async () => {
    const values = await form.validateFields()
    await sendBulkEmails({
      recipients,
      subject: values.subject,
      body: values.body
    })
    handleClose()
  }

  const previewName = previewRecipient
    ? [previewRecipient.firstName, previewRecipient.lastName].filter(Boolean).join(' ').trim() || 'Colleague'
    : 'Colleague'

  return (
    <Drawer
      title='Send bulk email'
      visible={visible}
      onClose={handleClose}
      width={720}
      destroyOnClose
      footer={(
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type='primary' className='bg-blue-400' loading={loading} onClick={handleSubmit}>
            Send to {recipientsWithEmail || recipientCount} recipient{recipientsWithEmail === 1 ? '' : 's'}
          </Button>
        </Space>
      )}
    >
      <Alert
        className='mb-4'
        type='info'
        message={`${recipientCount} selected employee${recipientCount === 1 ? '' : 's'}`}
        description={recipientsWithEmail < recipientCount
          ? `${recipientsWithEmail} have an email address. Employees without email will be skipped.`
          : 'Each person will receive their own email.'}
      />
      <Form
        form={form}
        layout='vertical'
        onValuesChange={(_, values) => updatePreview(values.body)}
      >
        <Form.Item
          label='Subject'
          name='subject'
          rules={[{ required: true, message: 'Please enter a subject.' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label='Message'
          name='body'
          rules={[{ required: true, message: 'Please enter a message.' }]}
          extra="Use {{ name }} for the recipient's full name. You can also use {{ firstName }} and {{ lastName }}."
        >
          <Input.TextArea rows={16} />
        </Form.Item>
      </Form>
      {previewRecipient && previewBody && (
        <div className='mt-6'>
          <Text type='secondary'>Preview for {previewName}</Text>
          <Paragraph className='mt-2 whitespace-pre-wrap border rounded p-4 bg-gray-50 mb-0'>
            {previewBody}
          </Paragraph>
        </div>
      )}
    </Drawer>
  )
}
